import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ChevronDown, ChevronRight } from 'lucide-react';
import { memo, useCallback, useState } from 'react';
import { ParameterCell } from './ParameterCell';
// ─── Format Currency ──────────────────────────────────────────────────────
export function formatCurrency(value) {
    if (value === 0) return '—';
    if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(1)}k`;
    return `$${value.toLocaleString(undefined, {
        maximumFractionDigits: 0
    })}`;
}
// ─── Inline FTE Editor ──────────────────────────────────────────────────────
export function InlineFteEditor({ value, onChange }) {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState('');
    const handleStart = useCallback(()=>{
        setEditValue(value != null ? String(value) : '');
        setIsEditing(true);
    }, [
        value
    ]);
    const handleBlur = useCallback(()=>{
        setIsEditing(false);
        if (editValue === '') {
            onChange(undefined);
            return;
        }
        const parsed = Number(editValue);
        if (Number.isNaN(parsed) || parsed < 0) return;
        if (parsed !== value) {
            onChange(parsed);
        }
    }, [
        editValue,
        value,
        onChange
    ]);
    const handleKeyDown = useCallback((e)=>{
        if (e.key === 'Enter') {
            e.target.blur();
        } else if (e.key === 'Escape') {
            setIsEditing(false);
        }
    }, []);
    if (isEditing) {
        return /*#__PURE__*/ _jsx("input", {
            type: "number",
            value: editValue,
            onChange: (e)=>setEditValue(e.target.value),
            onBlur: handleBlur,
            onKeyDown: handleKeyDown,
            className: "w-full px-2 py-1 text-sm border border-blue-400 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-right",
            step: "any",
            min: "0"
        });
    }
    return /*#__PURE__*/ _jsx("div", {
        className: "px-2 py-1.5 text-sm text-right rounded cursor-pointer hover:bg-gray-50",
        onClick: handleStart,
        children: value != null ? value : '—'
    });
}
// ─── Line Row ──────────────────────────────────────────────────────────────
export const LineRow = /*#__PURE__*/ memo(function LineRow({ line, columns, periods, onParamChange, onFteChange, onLocationChange, locationVariants }) {
    return /*#__PURE__*/ _jsx("tr", {
        className: "border-b border-gray-100 hover:bg-gray-50/50",
        children: columns.map((col)=>{
            if (col.key === 'label') {
                return /*#__PURE__*/ _jsx("td", {
                    className: "px-3 py-2 text-sm font-medium text-gray-900",
                    children: line.label
                }, col.key);
            }
            if (col.key === 'ftes') {
                return /*#__PURE__*/ _jsx("td", {
                    className: "px-1 py-1",
                    children: /*#__PURE__*/ _jsx(InlineFteEditor, {
                        value: line.fteCount,
                        onChange: (val)=>onFteChange?.(line.id, val)
                    })
                }, col.key);
            }
            if (col.key === 'location' && locationVariants?.length) {
                return /*#__PURE__*/ _jsx("td", {
                    className: "px-1 py-1",
                    children: /*#__PURE__*/ _jsxs("select", {
                        value: line.location || '',
                        onChange: (e)=>{
                            const val = e.target.value;
                            onLocationChange?.(line.id, val || undefined);
                        },
                        className: "w-full px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500",
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "",
                                children: "—"
                            }),
                            locationVariants.map((loc)=>/*#__PURE__*/ _jsx("option", {
                                    value: loc,
                                    children: loc === 'on_site' ? 'On-Site' : 'Off-Site'
                                }, loc))
                        ]
                    })
                }, col.key);
            }
            const param = line.parameters[col.parameterName];
            if (!param) {
                return /*#__PURE__*/ _jsx("td", {
                    className: "px-1 py-1 text-sm text-gray-400 text-right",
                    children: "—"
                }, col.key);
            }
            if (col.perPeriod && periods.length > 0) {
                return periods.map((period)=>/*#__PURE__*/ _jsx("td", {
                        className: "px-1 py-1",
                        children: /*#__PURE__*/ _jsx(ParameterCell, {
                            param: param,
                            editable: col.editable,
                            period: period,
                            onChange: (val)=>onParamChange(line.id, col.parameterName, val, period)
                        })
                    }, `${col.key}-${period}`));
            }
            return /*#__PURE__*/ _jsx("td", {
                className: "px-1 py-1",
                children: /*#__PURE__*/ _jsx(ParameterCell, {
                    param: param,
                    editable: col.editable,
                    onChange: (val)=>onParamChange(line.id, col.parameterName, val)
                })
            }, col.key);
        })
    });
});
// ─── Group Section ──────────────────────────────────────────────────────────
export const GroupSection = /*#__PURE__*/ memo(function GroupSection({ group, columns, periods, collapsible, onParamChange, onFteChange, onLocationChange, locationVariants }) {
    const [expanded, setExpanded] = useState(true);
    // Partition constraint check
    const pc = group.partitionConstraints;
    const currentFtes = group.lines.reduce((s, l)=>s + (l.fteCount ?? 0), 0);
    const fteOutOfRange = pc && (pc.minFtes != null && currentFtes < pc.minFtes || pc.maxFtes != null && currentFtes > pc.maxFtes);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("tr", {
                className: "bg-gray-50 border-b border-gray-200",
                children: /*#__PURE__*/ _jsx("td", {
                    colSpan: columns.reduce((n, c)=>n + (c.perPeriod ? Math.max(periods.length, 1) : 1), 0),
                    className: "px-3 py-2",
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2",
                        children: [
                            /*#__PURE__*/ _jsxs("button", {
                                className: "flex items-center gap-1.5 text-sm font-semibold text-gray-700",
                                onClick: ()=>collapsible && setExpanded(!expanded),
                                disabled: !collapsible,
                                children: [
                                    collapsible && (expanded ? /*#__PURE__*/ _jsx(ChevronDown, {
                                        className: "w-4 h-4"
                                    }) : /*#__PURE__*/ _jsx(ChevronRight, {
                                        className: "w-4 h-4"
                                    })),
                                    group.label,
                                    /*#__PURE__*/ _jsxs("span", {
                                        className: "text-xs text-gray-400 font-normal ml-2",
                                        children: [
                                            group.lines.length,
                                            " ",
                                            group.lines.length === 1 ? 'role' : 'roles'
                                        ]
                                    })
                                ]
                            }),
                            pc && /*#__PURE__*/ _jsxs("span", {
                                className: `text-[10px] px-1.5 py-0.5 rounded-full border ${fteOutOfRange ? 'bg-red-50 text-red-600 border-red-300' : 'bg-gray-100 text-gray-500 border-gray-300'}`,
                                children: [
                                    group.groupType === 'dedicated' ? 'Dedicated' : group.groupType === 'shared' ? 'Shared' : '',
                                    ' ',
                                    pc.minFtes != null && pc.maxFtes != null ? pc.minFtes === pc.maxFtes ? `${pc.minFtes} FTEs` : `${pc.minFtes}-${pc.maxFtes} FTEs` : pc.minFtes != null ? `≥${pc.minFtes} FTEs` : `≤${pc.maxFtes} FTEs`,
                                    fteOutOfRange && ` (current: ${currentFtes})`
                                ]
                            })
                        ]
                    })
                })
            }),
            expanded && group.lines.map((line)=>/*#__PURE__*/ _jsx(LineRow, {
                    line: line,
                    columns: columns,
                    periods: periods,
                    onParamChange: onParamChange,
                    onFteChange: onFteChange,
                    onLocationChange: onLocationChange,
                    locationVariants: locationVariants
                }, line.id)),
            expanded && group.subtotal && /*#__PURE__*/ _jsxs("tr", {
                className: "border-b border-gray-200 bg-gray-50/50",
                children: [
                    /*#__PURE__*/ _jsx("td", {
                        className: "px-3 py-1.5 text-sm font-medium text-gray-600",
                        children: "Subtotal"
                    }),
                    columns.slice(1).map((col)=>/*#__PURE__*/ _jsx("td", {
                            className: "px-2 py-1.5 text-sm text-right font-medium text-gray-700",
                            children: col.parameterName === 'total' && group.subtotal ? formatCurrency(group.subtotal.value ?? 0) : ''
                        }, col.key))
                ]
            })
        ]
    });
});
// ─── Cost Summary ──────────────────────────────────────────────────────────
export function CostSummary({ clinPricing }) {
    const hasPeriodTotals = Object.keys(clinPricing.periodTotals).length > 0;
    if (!hasPeriodTotals && clinPricing.grandTotal === 0) return null;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            hasPeriodTotals && /*#__PURE__*/ _jsxs("div", {
                className: "pt-2 border-t border-gray-200 space-y-1",
                children: [
                    Object.entries(clinPricing.periodTotals).map(([period, pt])=>/*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center gap-4 text-xs text-gray-500",
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "font-medium text-gray-600 w-24 truncate",
                                    children: period
                                }),
                                /*#__PURE__*/ _jsxs("span", {
                                    children: [
                                        "Labor: ",
                                        formatCurrency(pt.laborCost)
                                    ]
                                }),
                                pt.indirectCost > 0 && /*#__PURE__*/ _jsxs("span", {
                                    children: [
                                        "Indirect: ",
                                        formatCurrency(pt.indirectCost)
                                    ]
                                }),
                                pt.feeCost > 0 && /*#__PURE__*/ _jsxs("span", {
                                    children: [
                                        "Fee: ",
                                        formatCurrency(pt.feeCost)
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("span", {
                                    className: "font-medium text-gray-700",
                                    children: [
                                        "Total: ",
                                        formatCurrency(pt.totalCost)
                                    ]
                                })
                            ]
                        }, period)),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex justify-end pt-1",
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: "text-sm",
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "text-gray-500 mr-2",
                                    children: "CLIN Total:"
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    className: "font-semibold text-gray-900",
                                    children: formatCurrency(clinPricing.grandTotal)
                                })
                            ]
                        })
                    })
                ]
            }),
            !hasPeriodTotals && clinPricing.grandTotal > 0 && /*#__PURE__*/ _jsx("div", {
                className: "flex justify-end pt-2 border-t border-gray-200",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "text-sm",
                    children: [
                        /*#__PURE__*/ _jsx("span", {
                            className: "text-gray-500 mr-2",
                            children: "CLIN Total:"
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            className: "font-semibold text-gray-900",
                            children: formatCurrency(clinPricing.grandTotal)
                        })
                    ]
                })
            })
        ]
    });
}
